import { z } from 'zod'

import type { SummaryQualityStats } from './generated/api'
import { parseResponse, type SummaryFeedbackStatsBucket } from './meetingSummaryFeedback'
import { fetchAPI } from '.'

const timestamp = z.string().datetime({ offset: true })
const count = z.number().int().nonnegative()
const usd = z.number().nonnegative()
const version = { promptVersion: z.string().nullable(), promptLabel: z.string().nullable(), model: z.string() }

const percentilesSchema = z.object({
  p50: z.number().nonnegative().nullable(),
  p95: z.number().nonnegative().nullable(),
  p99: z.number().nonnegative().nullable(),
  measured: count,
})

const modelCostSchema = z.object({
  model: z.string(),
  usd: usd.nullable(),
  runs: count,
  usdPerSummary: usd.nullable(),
})

const statsSchema: z.ZodType<SummaryQualityStats, unknown> = z.object({
  summaries: z.object({
    overall: count,
    buckets: z.array(z.object({ start: timestamp, count })).default([]),
    byVersion: z.array(z.object({ ...version, count })).default([]),
  }),
  latency: z.object({
    overall: percentilesSchema,
    buckets: z.array(percentilesSchema.extend({ start: timestamp })).default([]),
    byVersion: z.array(percentilesSchema.extend(version)).default([]),
  }),
  cost: z.object({
    overallUsd: usd,
    usdPerSummary: usd.nullable(),
    unpricedRuns: count,
    buckets: z.array(z.object({ start: timestamp, byModel: z.array(modelCostSchema).default([]) })).default([]),
    byVersion: z
      .array(modelCostSchema.extend({ promptVersion: version.promptVersion, promptLabel: version.promptLabel }))
      .default([]),
    candidates: z.array(modelCostSchema.extend({ start: timestamp })).default([]),
  }),
})

export const getSummaryQualityStats = async (
  {
    since,
    until,
    bucket,
    promptVersion,
    summaryModel,
  }: {
    since: string
    until: string
    bucket: SummaryFeedbackStatsBucket
    promptVersion?: string
    summaryModel?: string
  },
  signal?: AbortSignal
) => {
  const response = await fetchAPI<unknown>({
    method: 'GET',
    url: '/api/v1/internal/summary-quality/stats',
    params: {
      since,
      until,
      bucket,
      ...(promptVersion ? { promptVersion } : {}),
      ...(summaryModel ? { summaryModel } : {}),
    },
    signal,
    expectedStatuses: [403],
  })
  return parseResponse(statsSchema, response.data, 'Summary quality')
}
